import {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useFunctionalAreaContext } from '../../../../contexts/FunctionalAreaContext';
import { UserContext } from '../../../../contexts/UserContext';
import {
  useFunctionalAreaManager,
  useApplicationRefresh,
  useTableState,
  useDialogState,
} from '../hooks';
import { useDataLeadManagement } from '../hooks/useDataLeadManagement';
import { ManageDataLeadsPresenter } from './ManageDataLeadsPresenter';
import { FunctionalAreasQuery } from '../queries';
import { getEnhancedDataLeads } from '../utils/dataLeadUtils';
import type { FunctionalArea } from '../types';

/**
 * Container component for Data Lead Management
 * Handles all business logic, data fetching, and state management
 * Delegates UI rendering to the ManageDataLeadsPresenter component
 */
export const ManageDataLeadsContainer: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useContext(UserContext);
  const { functionalAreas: userFunctionalAreas } = useFunctionalAreaContext();

  // Admin toggle state - defaults to false (show user's functional areas)
  const [showAllFunctionalAreas, setShowAllFunctionalAreas] = useState(false);

  // State for all functional areas (fetched when admin toggles on)
  const [allFunctionalAreas, setAllFunctionalAreas] = useState<
    FunctionalArea[]
  >([]);

  // Determine which functional areas to use based on admin toggle
  const functionalAreas = useMemo(() => {
    if (isAdmin && showAllFunctionalAreas) {
      return allFunctionalAreas;
    }
    return userFunctionalAreas;
  }, [
    isAdmin,
    showAllFunctionalAreas,
    allFunctionalAreas,
    userFunctionalAreas,
  ]);

  // Ref to store successful application IDs for reset after refresh
  const successfulAppIdsRef = useRef<number[]>([]);
  // Ref to track last processed application IDs to prevent duplicate processing
  const lastProcessedAppIdsRef = useRef<string>('');
  // Ref to store the initializeDataLeadAssignments function to avoid dependency issues
  const initializeDataLeadAssignmentsRef = useRef<
    ((apps: any[]) => void) | null
  >(null);

  // GraphQL hooks - the functional area manager handles its own queries
  const [, { data }] = useLazyQuery(FunctionalAreasQuery, {
    fetchPolicy: 'cache-and-network',
  });

  // Query to fetch ALL functional areas (for admins) - only fetch metadata, not applications
  const [fetchAllFunctionalAreas, { loading: loadingAllFunctionalAreas }] =
    useLazyQuery(FunctionalAreasQuery, {
      fetchPolicy: 'cache-and-network',
      onCompleted: allData => {
        if (allData?.devhub?.connections?.functionalAreas) {
          // Store all functional areas (applications will be loaded on-demand per area)
          setAllFunctionalAreas(allData.devhub.connections.functionalAreas);
        }
      },
    });

  // Initialize hooks with GraphQL data and simplified data manager interface
  const functionalAreaManager = useFunctionalAreaManager({
    dataLeadManager: {
      hasRowChanges: () => false,
      initializeDataLeadAssignments: () => {},
    },
    data, // Pass GraphQL data so the hook can auto-select functional areas
  });

  // Handler for admin toggle
  const handleAdminToggle = useCallback(
    (checked: boolean) => {
      setShowAllFunctionalAreas(checked);

      // Clear the selected functional area when toggling in either direction
      // This forces the user to explicitly select a functional area from the new list
      functionalAreaManager.setSelectedFunctionalArea('');
      functionalAreaManager.setActiveFunctionalAreaApps([]);

      // If toggling ON and we haven't fetched all areas yet, fetch them
      if (checked && allFunctionalAreas.length === 0) {
        fetchAllFunctionalAreas({
          variables: {
            // Use ids: null filter to fetch all functional areas
            filter: {
              ids: null,
            },
            rolesFilter2: {
              roleId: 5, // Data Lead role ID (singular, not plural)
            },
            pagination: null, // No pagination limit on functional areas
            applicationsPagination: {
              limit: 1, // Fetch just 1 app per area to verify structure, apps loaded on-demand
              offset: 0,
            },
          },
        });
      }
    },
    [allFunctionalAreas.length, fetchAllFunctionalAreas, functionalAreaManager],
  );

  // Effect to trigger initial data fetch when functional areas are available
  useEffect(() => {
    if (
      functionalAreas.length > 0 &&
      !functionalAreaManager.selectedFunctionalArea
    ) {
      // If there's only one functional area, auto-select it
      if (functionalAreas.length === 1) {
        const firstArea = functionalAreas[0];
        const areaName = firstArea.name || '';
        if (areaName) {
          functionalAreaManager.performFunctionalAreaTransition(areaName);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Effect should only run when specific values change
  }, [
    functionalAreas,
    functionalAreaManager.selectedFunctionalArea,
    functionalAreaManager.performFunctionalAreaTransition,
    functionalAreaManager.activeFunctionalAreaApps.length,
  ]);

  // Initialize data lead management with actual data
  const dataLeadManager = useDataLeadManagement(
    functionalAreaManager.activeFunctionalAreaApps,
    functionalAreaManager.completeFunctionalAreasData,
    functionalAreaManager.selectedFunctionalArea,
  );

  // Store the function in a ref to avoid dependency issues
  initializeDataLeadAssignmentsRef.current =
    dataLeadManager.initializeDataLeadAssignments;

  // Effect to re-initialize data lead assignments when apps or functional area data changes
  useEffect(() => {
    if (
      functionalAreaManager.activeFunctionalAreaApps.length > 0 &&
      functionalAreaManager.completeFunctionalAreasData.length > 0 &&
      successfulAppIdsRef.current.length === 0 && // Don't reinitialize during refresh/reset cycle
      initializeDataLeadAssignmentsRef.current
    ) {
      initializeDataLeadAssignmentsRef.current(
        functionalAreaManager.activeFunctionalAreaApps,
      );
    }
  }, [
    functionalAreaManager.activeFunctionalAreaApps,
    functionalAreaManager.completeFunctionalAreasData,
  ]);

  // Effect to reset successful changes after applications have been refreshed
  useEffect(() => {
    // Only run this reset if we have stored successful app IDs (indicating a recent submission)
    if (
      successfulAppIdsRef.current.length > 0 &&
      functionalAreaManager.activeFunctionalAreaApps.length > 0
    ) {
      // Reset the successful changes with the updated applications
      dataLeadManager.resetSuccessfulChanges(
        successfulAppIdsRef.current,
        functionalAreaManager.activeFunctionalAreaApps,
      );

      // Clear the ref so we don't reset again
      successfulAppIdsRef.current = [];
    }
  }, [
    functionalAreaManager.activeFunctionalAreaApps,
    dataLeadManager.resetSuccessfulChanges,
    dataLeadManager,
  ]);

  // Memoize the dataLeadManager object to prevent unnecessary re-renders
  const memoizedDataLeadManager = useMemo(
    () => ({
      clearPendingChanges: dataLeadManager.clearPendingChanges,
    }),
    [dataLeadManager.clearPendingChanges],
  );

  const applicationRefresh = useApplicationRefresh({
    selectedFunctionalArea: functionalAreaManager.selectedFunctionalArea,
    setCompleteFunctionalAreasData:
      functionalAreaManager.setCompleteFunctionalAreasData,
    dataLeadManager: memoizedDataLeadManager,
  });

  // Helper to check if a data lead is assigned
  const isDataLeadAssigned = useCallback(
    (applicationId: number, leadLogin: string): boolean => {
      const assignments =
        dataLeadManager.dataLeadAssignments.get(applicationId);
      return assignments ? assignments.has(leadLogin) : false;
    },
    [dataLeadManager.dataLeadAssignments],
  );

  // Compute matrix table rows from functional area data
  const matrixTableRows = useMemo(() => {
    return functionalAreaManager.activeFunctionalAreaApps.map(app => {
      const row: any = {
        applicationId: app.applicationId,
        name: app.name,
        description: app.description || '',
      };

      // Add data lead columns dynamically
      const functionalAreaDataLeads = getEnhancedDataLeads(
        functionalAreaManager.completeFunctionalAreasData,
        functionalAreaManager.selectedFunctionalArea,
      );

      functionalAreaDataLeads.forEach(lead => {
        if (lead.login) {
          row[lead.login] = isDataLeadAssigned(app.applicationId, lead.login);
        }
      });

      return row;
    });
  }, [
    functionalAreaManager.activeFunctionalAreaApps,
    functionalAreaManager.selectedFunctionalArea,
    functionalAreaManager.completeFunctionalAreasData,
    isDataLeadAssigned,
  ]);

  const tableState = useTableState({ matrixTableRows });
  const dialogState = useDialogState();

  // Get functional area data leads
  const functionalAreaDataLeads = useMemo(() => {
    return getEnhancedDataLeads(
      functionalAreaManager.completeFunctionalAreasData,
      functionalAreaManager.selectedFunctionalArea,
    );
  }, [
    functionalAreaManager.completeFunctionalAreasData,
    functionalAreaManager.selectedFunctionalArea,
  ]);

  // Create template props for data table
  const templateProps = useMemo(
    () => ({
      // Original props for backward compatibility
      dataLeadManager,
      selectedAppsForUpdate: tableState.selectedAppsForUpdate,
      setSelectedAppsForUpdate: tableState.setSelectedAppsForUpdate,
      functionalAreaDataLeads,
      activeFunctionalAreaApps: functionalAreaManager.activeFunctionalAreaApps,
      dialogState,
      applicationRefresh,

      // Template interface requirements
      dataLeadAssignments: dataLeadManager.dataLeadAssignments,
      functionalAreasData: functionalAreaManager.completeFunctionalAreasData,
      selectedFunctionalArea: functionalAreaManager.selectedFunctionalArea,
      applicationModes: dataLeadManager.applicationModes,
      hasRowChanges: dataLeadManager.hasRowChanges,
      onToggleAssignment: dataLeadManager.toggleDataLeadAssignment,
      onResetRow: dataLeadManager.resetRowToOriginal,
    }),
    [
      dataLeadManager,
      tableState.selectedAppsForUpdate,
      tableState.setSelectedAppsForUpdate,
      functionalAreaDataLeads,
      functionalAreaManager.activeFunctionalAreaApps,
      functionalAreaManager.completeFunctionalAreasData,
      functionalAreaManager.selectedFunctionalArea,
      dialogState,
      applicationRefresh,
    ],
  );

  // Handlers
  const handleExecuteSelectAll = useCallback(
    async (leadLogin: string, rows: any[], targetValue: boolean) => {
      try {
        for (const row of rows) {
          const currentValue = isDataLeadAssigned(row.applicationId, leadLogin);
          if (currentValue !== targetValue) {
            // Use the toggleDataLeadAssignment method from the hook
            dataLeadManager.toggleDataLeadAssignment(
              row.applicationId,
              leadLogin,
              targetValue,
            );
          }
        }

        // Refresh data after bulk changes
        await applicationRefresh.refreshSpecificApplications([]);
        dialogState.closeSelectAllDialog();
      } catch (error) {
        // eslint-disable-next-line no-console -- Error logging
        console.error('Error executing select all:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataLeadManager methods are stable
    [applicationRefresh, dialogState, isDataLeadAssigned],
  );

  const handleConfirmExit = useCallback(() => {
    dialogState.closeCancelConfirmDialog();
    navigate('/my-apps');
  }, [dialogState, navigate]);

  const handleCancelClick = useCallback(() => {
    // Check if there are any changes made
    const hasChanges = functionalAreaManager.activeFunctionalAreaApps.some(
      app => dataLeadManager.hasRowChanges(app.applicationId),
    );

    if (hasChanges) {
      // Show confirmation dialog if there are changes
      dialogState.setShowCancelConfirmDialog(true);
    } else {
      // Navigate directly if no changes
      navigate('/my-apps');
    }
  }, [
    functionalAreaManager.activeFunctionalAreaApps,
    dataLeadManager,
    dialogState,
    navigate,
  ]);

  // Memoized callback for processing completion to prevent re-renders
  const handleProcessingComplete = useCallback(
    (successfulApplicationIds: number[]) => {
      const timestamp = new Date().toISOString();
      // eslint-disable-next-line no-console -- High-level debugging for processing complete timing in development
      console.log(
        `🎯 [${timestamp}] handleProcessingComplete called with:`,
        successfulApplicationIds,
      );

      // Allow immediate refreshes for each completed application - no duplicate prevention needed
      // since refreshing the same application multiple times (after create then after delete) is valid UX

      // Store the successful application IDs for later reset
      successfulAppIdsRef.current = successfulApplicationIds;

      // Trigger refresh immediately when processing completes (while dialog is still open)
      if (successfulApplicationIds.length > 0) {
        // eslint-disable-next-line no-console -- High-level debugging for refresh trigger timing in development
        console.log(
          `🔄 [${new Date().toISOString()}] About to call refreshSpecificApplications for:`,
          successfulApplicationIds,
        );
        applicationRefresh
          .refreshSpecificApplications(successfulApplicationIds)
          .then(() => {
            // eslint-disable-next-line no-console -- High-level debugging for refresh completion timing in development
            console.log(
              `✅ [${new Date().toISOString()}] refreshSpecificApplications completed successfully`,
            );
          })
          .catch(error => {
            // eslint-disable-next-line no-console -- Error logging for refresh timing debugging in development
            console.error(
              `❌ [${new Date().toISOString()}] Failed to refresh applications after mutation:`,
              error,
            );
          });
      }
    },
    [applicationRefresh],
  );

  // Pass all data and handlers to the presenter
  return (
    <ManageDataLeadsPresenter
      // Functional Area Data
      functionalAreas={functionalAreas.map((fa: any) => ({
        name: fa.name || fa.displayName || '',
        applications: fa.applications || [],
        dataLeads: fa.dataLeads || [],
      }))}
      selectedFunctionalArea={functionalAreaManager.selectedFunctionalArea}
      activeFunctionalAreaApps={functionalAreaManager.activeFunctionalAreaApps}
      completeFunctionalAreasData={
        functionalAreaManager.completeFunctionalAreasData
      }
      functionalAreaDataLeads={functionalAreaDataLeads}
      // Admin Functionality
      isAdmin={isAdmin}
      showAllFunctionalAreas={showAllFunctionalAreas}
      onAdminToggle={handleAdminToggle}
      // Loading States
      isRefreshing={applicationRefresh.isRefreshing}
      isFetchingApplicationsForArea={
        functionalAreaManager.isFetchingApplicationsForArea
      }
      isLoadingFunctionalAreas={loadingAllFunctionalAreas}
      // Table State
      matrixTableRows={matrixTableRows}
      globalFilterValue={tableState.globalFilterValue}
      currentPageSize={tableState.currentPageSize}
      selectedAppsForUpdate={tableState.selectedAppsForUpdate}
      // Dialog States
      showSelectAllDialog={dialogState.showSelectAllDialog}
      selectAllDialogData={dialogState.selectAllDialogData}
      showSwitchConfirmDialog={dialogState.showSwitchConfirmDialog}
      showCancelConfirmDialog={dialogState.showCancelConfirmDialog}
      showChangesReviewDialog={dialogState.showChangesReviewDialog}
      showSubmissionReview={dialogState.showSubmissionReview}
      submitChangesViewMode={dialogState.submitChangesViewMode}
      // Data Manager
      dataLeadManager={{
        hasRowChanges: dataLeadManager.hasRowChanges,
        applicationModes: dataLeadManager.applicationModes,
        getChangesSummary: dataLeadManager.getChangesSummary,
        dataLeadAssignments: dataLeadManager.dataLeadAssignments,
        resetSuccessfulChanges: dataLeadManager.resetSuccessfulChanges,
      }}
      // Event Handlers
      onFunctionalAreaChange={
        functionalAreaManager.handleFunctionalAreaChangeWithConfirmation
      }
      onFilterChange={tableState.setGlobalFilterValue}
      onRefresh={() => {
        // Get all current application IDs to refresh
        const allApplicationIds =
          functionalAreaManager.activeFunctionalAreaApps.map(
            app => app.applicationId,
          );
        // eslint-disable-next-line no-console -- High-level debugging for manual refresh operations in development
        console.log(
          `🔄 [${new Date().toISOString()}] Manual refresh button clicked - refreshing all applications:`,
          allApplicationIds,
        );
        applicationRefresh.refreshSpecificApplications(allApplicationIds);
      }}
      onMatrixPageChange={tableState.handleMatrixPageChange}
      onMatrixSelectionChange={tableState.handleMatrixSelectionChange}
      onExecuteSelectAll={handleExecuteSelectAll}
      onStayOnPage={dialogState.closeCancelConfirmDialog}
      onConfirmExit={handleConfirmExit}
      onConfirmSwitch={() => {
        functionalAreaManager.confirmFunctionalAreaSwitch();
        dialogState.closeSwitchConfirmDialog();
      }}
      onCancelSwitch={dialogState.closeSwitchConfirmDialog}
      onCloseSelectAllDialog={dialogState.closeSelectAllDialog}
      onCancel={handleCancelClick}
      onResetToOriginal={() => {
        dataLeadManager.initializeDataLeadAssignments(
          functionalAreaManager.activeFunctionalAreaApps,
        );
      }}
      onSubmit={() => {
        dialogState.setShowChangesReviewDialog(true);
      }}
      onCloseChangesReviewDialog={dialogState.closeChangesReviewDialog}
      onConfirmChangesReview={() => {
        dialogState.closeChangesReviewDialog();
        dialogState.setShowSubmissionReview(true);
      }}
      onViewModeChange={dialogState.setSubmitChangesViewMode}
      onCloseSubmissionReview={dialogState.closeSubmissionReview}
      onProcessingComplete={handleProcessingComplete}
      onConfirmSubmissionReview={() => {
        dialogState.closeSubmissionReview();
        // Clear the processed tracking ref for future submissions
        lastProcessedAppIdsRef.current = '';
        // Refresh is now triggered by onProcessingComplete, so just close the dialog
      }}
      // Additional Data
      templateProps={templateProps}
    />
  );
};

/*
ARCHITECTURE BENEFITS DEMONSTRATED:

✅ Separation of Concerns:
- Business Logic: Isolated in custom hooks (useFunctionalAreaManager, useApplicationRefresh, etc.)
- UI Components: Pure presentational components (ApplicationsTable, SelectAllDialog, etc.)
- Container Logic: Orchestrates hooks and components in this container

✅ Reusability:
- Hooks can be reused in other data lead management contexts
- UI components can be reused with different data sources
- Clear interfaces make testing and maintenance easier

✅ Maintainability:
- Each component has a single responsibility
- Props are explicitly typed with TypeScript interfaces
- Easy to modify individual pieces without affecting others

✅ Testability:
- Hooks can be unit tested independently
- Components can be tested with mock props
- Container logic is simplified and focused

NEXT STEPS:
1. Implement missing data lead manager functionality
2. Complete template props configuration
3. Add proper error handling and loading states
4. Integrate with actual GraphQL queries and mutations
5. Add comprehensive tests for hooks and components
*/
